import { Inject, Injectable, Optional } from "@angular/core";

import { combineLatest, Observable, of } from "rxjs";
import { catchError, filter, first, map, switchMap } from "rxjs/operators";

import { dbToInt, dbToString } from "@dffedb/util";

import { APP_SHELL_LAYOUT_OPTIONS, AppShellLayoutOptions } from "../app-shell/app-shell-layout.token";
import { BrugerRepository, Rolle } from "../bruger";
import { AktuelBrugerService } from "../bruger/aktuel-bruger.service";
import { AppserverCredentials } from "../credentials";
import { ForbrugerTilknytning, ForbrugerTilknytningRepository } from "../forbruger-tilknytning";
import { ForsyningIndstillinger, ForsyningIndstillingerService } from "../forsyning-indstillinger";
import { BrugerHttpClient, EksternForsyningHttpClient, ParametreForAktuelleInstallation } from "../http";
import { Installation } from "./model/installation.model";

export interface InstallationAfgraensning {
    skip?: number;
    take?: number;
    huskelisteId?: number;
    soegetekst?: string;
    ejendomNr?: number;
}

const defaultInstallationAfgraensning: InstallationAfgraensning = {
    skip: 0,
    take: 10,
    huskelisteId: null,
    soegetekst: ""
};

@Injectable({ providedIn: "root" })
export class InstallationService {
    constructor(
        private readonly brugerHttp: BrugerHttpClient,
        private readonly eksternHttp: EksternForsyningHttpClient,
        private readonly brugerRepository: BrugerRepository,
        private readonly aktuelBrugerService: AktuelBrugerService,
        private readonly forbrugerTilknytningRepository: ForbrugerTilknytningRepository,
        private readonly forsyningIndstillingerService: ForsyningIndstillingerService,
        @Optional() @Inject(APP_SHELL_LAYOUT_OPTIONS) private readonly layoutOptions: AppShellLayoutOptions | null
    ) {}

    /** ServicePro NUI — `teknikerLoginRoute` sættes kun i `apps/servicepro`. */
    private get erServicePro(): boolean {
        return !!this.layoutOptions?.teknikerLoginRoute;
    }

    public hentInstallationerForEjendom(ejendomNr: number): Observable<Installation[]> {
        return this.hentInterneInstallationer({ ejendomNr }, true).pipe(map((r) => r.items));
    }

    public hentEgneInstallationer(parametre: InstallationAfgraensning = defaultInstallationAfgraensning): Observable<Installation[]> {
        return this.hentInterneInstallationer(parametre, false).pipe(map((r) => r.items));
    }

    public hentEgneOgTilknyttedeInterneInstallationer(
        parametre: InstallationAfgraensning = defaultInstallationAfgraensning
    ): Observable<HentInstallationerResultat> {
        return this.hentInterneInstallationer(parametre, true);
    }

    public hentInstallationer(
        parametre: InstallationAfgraensning = defaultInstallationAfgraensning
    ): Observable<HentInstallationerResultat> {
        // Vent på bruger — ellers kan pagineret liste (first()) låse et tomt resultat før tilknytninger er hentet.
        return this.aktuelBrugerService.select().pipe(
            filter((bruger) => !!bruger?.id),
            first(),
            switchMap(() => {
                const interneInstallationer$ = this.hentEgneOgTilknyttedeInterneInstallationer(parametre);
                const eksterneInstallationer$ = this.hentEksterneInstallationer();
                return combineLatest([interneInstallationer$, eksterneInstallationer$]).pipe(
                    map(([interne, eksterne]) => ({
                        eof: interne.eof,
                        items: [...interne.items, ...eksterne]
                    }))
                );
            })
        );
    }

    public hentEksterneInstallationer(): Observable<Installation[]> {
        return this.aktuelBrugerService.select().pipe(
            filter((bruger) => !!bruger?.id),
            first(),
            switchMap((bruger) =>
                this.forbrugerTilknytningRepository.hentTilknytninger(bruger.id, this.brugerHttp).pipe(
                    map((tilknytninger) => tilknytninger.filter((t) => t.erEksternTilknytning)),
                    switchMap((tilknytninger) => {
                        if (!tilknytninger.length) {
                            return of([] as Installation[]);
                        }
                        return combineLatest(tilknytninger.map((t) => this.hentEksterneInstallationerForTilknytning(t))).pipe(
                            map((grupper) => Array<Installation>().concat(...grupper))
                        );
                    })
                )
            )
        );
    }

    private hentEksterneInstallationerForTilknytning(tilknytning: ForbrugerTilknytning): Observable<Installation[]> {
        if (!tilknytning.credentials?.cryptId || tilknytning.erIkkeTilgaengelig) {
            return of([this.mapFejlendeTilknytning(tilknytning)]);
        }

        return this.eksternHttp.authenticate(tilknytning.credentials).pipe(
            switchMap((http) =>
                this.brugerRepository.hentBruger(http).pipe(
                    map((bruger) => ({
                        /* eslint-disable @typescript-eslint/naming-convention */
                        Soegetekst: "",
                        Skip: 0,
                        Take: 9999,
                        EBrugerId: bruger.id,
                        MedtagTilknyttede: false
                        /* eslint-enable @typescript-eslint/naming-convention */
                    })),
                    switchMap((params) => http.post("api/FindInstallationer", params, ParametreForAktuelleInstallation.false)),
                    map((installationer: any) =>
                        installationer.Installationer.map((i: any) =>
                            this.mapInstallation(i, tilknytning.forsyningIndstillinger, tilknytning.credentials)
                        )
                    )
                )
            ),
            catchError(() =>
                // Den eksterne bruger kan have ændret kodeord, hvilket vil medføre fejl.
                // Det skal ikke trække resten ned — markér tilknytningen i stedet.
                of([this.mapFejlendeTilknytning(tilknytning)])
            )
        );
    }

    private mapFejlendeTilknytning(tilknytning: ForbrugerTilknytning): Installation {
        return {
            adresse: tilknytning.forbrugerAdresse || "Ukendt adresse",
            postNrBy: "",
            aktivNr: 0,
            ejendomNr: tilknytning.ejendomNr || 0,
            installationNr: 0,
            maalerNr: "",
            maalerTypeTekst: "",
            navn: tilknytning.forbrugerNavn || "Tilknyttet forbruger",
            erIkkeTilgaengelig: true,
            fejlbesked: tilknytning.fejlbesked || "Kunne ikke hente tilknyttet installation (adgang nægtet).",
            forsyningIndstillinger: tilknytning.forsyningIndstillinger,
            credentials: tilknytning.credentials
        };
    }

    private hentInterneInstallationer(
        parametre: InstallationAfgraensning,
        medtagTilknyttede: boolean
    ): Observable<HentInstallationerResultat> {
        const credentials = this.brugerHttp.credentials;
        const forsyningIndstillinger$ = this.forsyningIndstillingerService.hentForsyningIndstillinger(credentials.forsyningId);
        const bruger$ = this.aktuelBrugerService.select();
        return combineLatest([forsyningIndstillinger$, bruger$]).pipe(
            filter(([, bruger]) => !!bruger?.id),
            switchMap(([forsyningIndstillinger, bruger]) => {
                const erForbruger = Rolle.erForbruger(bruger.rolle);
                // Forbruger: Hent alle installationer da vi ikke benytter paging og kan have eksterne tilknytninger.
                // Medarbejdere: Hent 10 (default) ad gangen.
                const take = erForbruger ? 9999 : parametre.take || defaultInstallationAfgraensning.take;
                const params = {
                    /* eslint-disable @typescript-eslint/naming-convention */
                    Soegetekst: parametre.soegetekst || defaultInstallationAfgraensning.soegetekst,
                    Skip: parametre.skip || defaultInstallationAfgraensning.skip,
                    Take: take + 1, // Vi henter en ekstra installation for at kunne bestemme 'eof'.
                    EBrugerId: bruger.id,
                    Huskeliste: bruger.huskelisteId || parametre.huskelisteId,
                    MedtagTilknyttede: medtagTilknyttede,
                    EjendomNr: parametre.ejendomNr,
                    ...(this.erServicePro ? { MedtagBeboer: "true" } : {})
                    /* eslint-enable @typescript-eslint/naming-convention */
                };
                return this.brugerHttp.post("api/FindInstallationer", params, ParametreForAktuelleInstallation.false).pipe(
                    map((result) => result.Installationer as any[]),
                    map((installationer) => installationer.map((i) => this.mapInstallation(i, forsyningIndstillinger, credentials))),
                    map((installationer) => ({
                        eof: installationer.length <= take,
                        items: installationer.splice(0, take) // Eventuel ekstra installation til eof-beregning fjernes den fra resultatet
                    }))
                );
            }),
            first()
        );
    }

    private mapInstallation(data: any, forsyningIndstillinger: ForsyningIndstillinger, credentials: AppserverCredentials): Installation {
        return {
            adresse: dbToString(data.Adresse),
            postNrBy: dbToString(data.PostNr) + " " + dbToString(data.By),
            aktivNr: dbToInt(data.AktivNr),
            ejendomNr: dbToInt(data.EjendomNr),
            installationNr: dbToInt(data.InstallationNr),
            maalerNr: dbToString(data["MålerNr"]),
            maalerTypeTekst: dbToString(data["Målertype"]),
            navn: foersteTekst(data.Navn, data.ForbrugerNavn, data.EBrugerNavn, data.BeboerNavn, data.Beboer, data.EjerNavn),
            telefonnr: foersteTekst(data.Telefon, data.Telefonnr, data.TelefonNr, data.Tlf, data.BeboerTelefon, data.BeboerTlf),
            forsyningIndstillinger,
            credentials
        };
    }
}

function foersteTekst(...values: unknown[]): string {
    for (const value of values) {
        if (value == null || value === "") {
            continue;
        }
        const tekst = String(value).trim();
        if (tekst) {
            return tekst;
        }
    }
    return "";
}

export interface HentInstallationerResultat {
    items: Installation[];
    eof: boolean;
}
